import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ConversationService, CONVERSATION_CONSTANTS } from '../services/conversation.service';
import { Conversation, ConversationFilters, ConversationFilterStatus } from '../models/conversation.model';
import { AuthService, UserRole } from '../auth/auth.service';
import { RCOversightService } from '../services/rc-oversight.service';
import { config } from '../config';

interface ContactGroup {
  contactId: string;
  contactName: string;
  contactMobile: string;
  avatarInitials: string;
  avatarColorIndex: number;
  totalUnread: number;
  conversations: Conversation[];
  isMC: boolean;
  mcCompanyName?: string;
}

@Component({
  selector: 'app-conversations',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="conversations-container">

      <!-- Header -->
      <div class="conversations-header">
        <div class="header-left">
          <h2>Conversations</h2>
          <span class="conversations-summary" *ngIf="!loading && contactGroups.length > 0">
            {{ getTotalConversationsCount() }} across {{ getTotalContactsCount() }} candidates
          </span>
        </div>
        <div class="header-right">
          <div class="filter-pill-wrap">
            <span class="filter-label">Filter:</span>
            <select
              [(ngModel)]="filters.status"
              (change)="handleFilterChange()"
              class="status-filter">
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Archived">Archived</option>
              <option value="Deleted">Deleted</option>
            </select>
          </div>
        </div>
      </div>

      <!-- List -->
      <div class="conversations-list">

        <div *ngIf="loading" class="loading-indicator">
          <span class="ap-spinner"></span>
          Loading conversations...
        </div>

        <div *ngIf="!loading && contactGroups.length === 0" class="empty-state">
          No conversations found
        </div>

        <!-- Contact groups -->
        <div
          *ngFor="let group of contactGroups; trackBy: trackByContactId"
          class="contact-group"
          [class.mc-group]="group.isMC">

          <!-- Group header row -->
          <div class="contact-group-header" (click)="toggleGroup(group.contactId)">
            <div class="contact-avatar" [attr.data-color]="group.avatarColorIndex">
              {{ group.avatarInitials }}
            </div>
            <div class="contact-info-col">
              <div class="contact-name-row">
                {{ group.contactName }}
                <span *ngIf="group.isMC" class="mc-badge">MC</span>
              </div>
              <div class="contact-subtitle">
                <span *ngIf="group.isMC" class="mc-contact-label">MC Contact&nbsp;·&nbsp;</span>{{ group.contactMobile || 'No mobile' }}&nbsp;·&nbsp;{{ group.conversations.length }}&nbsp;conversation{{ group.conversations.length !== 1 ? 's' : '' }}
              </div>
            </div>
            <div class="contact-group-meta">
              <span class="mc-company-name" *ngIf="group.isMC && group.mcCompanyName">{{ group.mcCompanyName }}</span>
              <span class="group-unread-badge" *ngIf="group.totalUnread > 0">
                {{ group.totalUnread > 9 ? '9+' : group.totalUnread }}
              </span>
              <svg class="group-chevron" [class.expanded]="isGroupExpanded(group.contactId)"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>

          <!-- Conversation sub-rows -->
          <div class="group-conversations" *ngIf="isGroupExpanded(group.contactId)">
            <div
              *ngFor="let conv of group.conversations; trackBy: trackByConvId"
              class="conversation-sub-row"
              [class.has-unread]="conv.unread_count && conv.unread_count > 0"
              [class.active]="isConversationActive(conv)"
              [class.read-only]="conv.isReadOnly"
              (click)="handleConversationClick(conv)">

              <!-- Channel chip -->
              <div class="conv-channel-chip" [ngClass]="'chip-' + getChannelType(conv.channel_id).toLowerCase()">
                <!-- WhatsApp -->
                <svg *ngIf="getChannelType(conv.channel_id) === 'WhatsApp'" class="chip-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.843L0 24l6.335-1.652A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.89 0-3.66-.498-5.185-1.367l-.371-.22-3.763.981.999-3.671-.242-.378A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
                <!-- Email -->
                <svg *ngIf="getChannelType(conv.channel_id) === 'Email'" class="chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                <!-- SMS -->
                <svg *ngIf="getChannelType(conv.channel_id) === 'SMS'" class="chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <!-- fallback icon -->
                <svg *ngIf="!['WhatsApp','Email','SMS'].includes(getChannelType(conv.channel_id))" class="chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span class="chip-label">{{ getChannelType(conv.channel_id) || 'SMS' }}</span>
              </div>

              <!-- Message preview -->
              <div class="conv-message">
                <span class="conv-message-text" *ngIf="conv.lastMessage">
                  &ldquo;{{ (conv.lastMessage.text || '') | slice:0:65 }}{{ (conv.lastMessage.text.length || 0) > 65 ? '\u2026' : '' }}&rdquo;
                </span>
                <span class="conv-message-placeholder" *ngIf="!conv.lastMessage">No messages yet</span>
              </div>

              <!-- Status + time -->
              <div class="conv-meta">
                <span class="status-badge" [ngClass]="conv.status">{{ conv.status }}</span>
                <span class="conv-time">{{ getRelativeTime(conv.lastMessage?.created_at || conv.updated_at || conv.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  `,
  styles: [`
    /* ── Container ── */
    .conversations-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      font-family: var(--ap-font, 'Inter', sans-serif);
    }

    /* ── Header ── */
    .conversations-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      gap: 10px;
      flex-wrap: wrap;
      flex-shrink: 0;
    }

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 8px;
      flex-wrap: wrap;
    }

    .conversations-header h2 {
      margin: 0;
      font-size: var(--ap-fs-h2, 17px);
      font-weight: 650;
      color: var(--ap-text, #0B1220);
      letter-spacing: -0.01em;
    }

    .conversations-summary {
      font-size: var(--ap-fs-micro, 11px);
      color: var(--ap-text-muted, #6B7280);
    }

    .header-right { display: flex; align-items: center; gap: 8px; }

    .filter-pill-wrap {
      display: flex;
      align-items: center;
      gap: 5px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      padding: 0 10px;
      height: var(--ap-btn-height, 32px);
      background: var(--ap-surface, #fff);
      box-shadow: var(--ap-shadow-sm);
    }

    .filter-label {
      font-size: var(--ap-fs-meta, 12px);
      color: var(--ap-text-muted, #6B7280);
      font-weight: 500;
      white-space: nowrap;
    }

    .status-filter {
      border: none;
      outline: none;
      font-size: var(--ap-fs-meta, 12px);
      font-weight: 550;
      background: transparent;
      color: var(--ap-text, #0B1220);
      cursor: pointer;
      padding: 0;
      height: auto;
      width: auto;
    }

    /* ── List scroll area ── */
    .conversations-list {
      flex: 1;
      overflow-y: auto;
      width: 100%;
    }

    .loading-indicator, .empty-state {
      padding: 32px 16px;
      text-align: center;
      color: var(--ap-text-muted, #6B7280);
      font-size: var(--ap-fs-body, 14px);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    /* ── Contact group wrapper ── */
    .contact-group {
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
    }

    /* ── Group header (contact row) ── */
    .contact-group-header {
      display: flex;
      align-items: center;
      padding: 11px 14px;
      gap: 10px;
      cursor: pointer;
      background: var(--ap-surface, #fff);
      transition: background 0.12s;
      user-select: none;
    }

    .contact-group-header:hover {
      background: var(--ap-surface-muted, #EEF0F3);
    }

    /* MC group — amber left accent */
    .contact-group.mc-group > .contact-group-header {
      border-left: 4px solid #B45309;
      background: linear-gradient(90deg, #FDF1DF 0%, #fff8f0 100%);
      padding-left: 11px;
    }
    .contact-group.mc-group > .contact-group-header:hover {
      background: linear-gradient(90deg, #f5e1c0 0%, #fdefd8 100%);
    }
    .contact-group.mc-group .contact-avatar {
      background: #B45309 !important;
      outline: 2px solid #F59E0B;
      outline-offset: 1px;
    }

    /* ── Avatar circle ── */
    .contact-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--ap-fs-meta, 12px);
      font-weight: 700;
      color: #fff;
      flex-shrink: 0;
      letter-spacing: 0;
    }

    .contact-avatar[data-color="0"] { background: var(--ap-primary, #2F5BDA); }
    .contact-avatar[data-color="1"] { background: var(--ap-success, #047A55); }
    .contact-avatar[data-color="2"] { background: #7C3AED; }
    .contact-avatar[data-color="3"] { background: var(--ap-warn, #B45309); }
    .contact-avatar[data-color="4"] { background: #0891B2; }
    .contact-avatar[data-color="5"] { background: #BE185D; }
    .contact-avatar[data-color="6"] { background: #374151; }

    /* ── Contact info column ── */
    .contact-info-col { flex: 1; min-width: 0; }

    .contact-name-row {
      font-size: var(--ap-fs-body, 14px);
      font-weight: 600;
      color: var(--ap-text, #0B1220);
      display: flex;
      align-items: center;
      gap: 5px;
      flex-wrap: wrap;
      line-height: 1.3;
    }

    .contact-subtitle {
      font-size: var(--ap-fs-micro, 11px);
      color: var(--ap-text-muted, #6B7280);
      margin-top: 1px;
      line-height: 1.3;
    }

    /* ── Group right-side meta ── */
    .contact-group-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
    }

    .mc-contact-label {
      color: #B45309;
      font-weight: 600;
    }

    .mc-company-name {
      font-size: var(--ap-fs-micro, 11px);
      color: var(--ap-warn, #B45309);
      font-weight: 500;
    }

    .group-unread-badge {
      background: var(--ap-danger, #B42318);
      color: #fff;
      border-radius: 99px;
      min-width: 19px;
      height: 19px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: var(--ap-fs-micro, 11px);
      font-weight: 700;
      padding: 0 5px;
      line-height: 1;
    }

    .group-chevron {
      width: 15px;
      height: 15px;
      color: var(--ap-text-muted, #6B7280);
      transition: transform 0.18s ease;
      transform: rotate(-90deg);
      flex-shrink: 0;
    }

    .group-chevron.expanded { transform: rotate(0deg); }

    /* ── Conversation sub-rows ── */
    .group-conversations {
      background: var(--ap-bg, #F6F7F9);
    }

    .conversation-sub-row {
      display: flex;
      align-items: center;
      padding: 9px 14px 9px 60px;
      gap: 10px;
      cursor: pointer;
      border-top: 1px solid var(--ap-border, #E3E6EB);
      transition: background 0.12s;
      box-sizing: border-box;
    }

    .conversation-sub-row:hover {
      background: var(--ap-surface-muted, #EEF0F3);
    }

    .conversation-sub-row.active {
      background: var(--ap-primary-soft, #E8EEFC);
    }

    /* Unread: blue left bar, bold text */
    .conversation-sub-row.has-unread {
      border-left: 2px solid var(--ap-primary, #2F5BDA);
      padding-left: 58px;
    }

    .conversation-sub-row.read-only { opacity: 0.8; }

    /* ── Channel chip ── */
    .conv-channel-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px 3px 6px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: var(--ap-fs-micro, 11px);
      font-weight: 600;
      white-space: nowrap;
      flex-shrink: 0;
      width: 88px;
      justify-content: center;
      border: 1px solid transparent;
    }

    .chip-icon {
      width: 11px;
      height: 11px;
      flex-shrink: 0;
    }

    /* WhatsApp — green tint */
    .conv-channel-chip.chip-whatsapp {
      background: var(--ap-success-soft, #E3F4EC);
      color: var(--ap-success, #047A55);
      border-color: #b8e1ce;
    }

    /* Email — blue tint (primary) */
    .conv-channel-chip.chip-email {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border-color: #c0cffa;
    }

    /* SMS — neutral muted */
    .conv-channel-chip.chip-sms {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
      border-color: var(--ap-border, #E3E6EB);
    }

    /* fallback */
    .conv-channel-chip:not(.chip-whatsapp):not(.chip-email):not(.chip-sms) {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
      border-color: var(--ap-border, #E3E6EB);
    }

    /* ── Message preview ── */
    .conv-message {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    .conv-message-text {
      font-size: var(--ap-fs-meta, 12px);
      color: var(--ap-text-soft, #4B5563);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      line-height: 1.4;
    }

    .conversation-sub-row.has-unread .conv-message-text {
      font-weight: 600;
      color: var(--ap-text, #0B1220);
    }

    .conv-message-placeholder {
      font-size: var(--ap-fs-meta, 12px);
      color: var(--ap-text-muted, #6B7280);
      font-style: italic;
    }

    /* ── Status + time meta ── */
    .conv-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
      flex-shrink: 0;
    }

    .conv-time {
      font-size: var(--ap-fs-micro, 11px);
      color: var(--ap-text-muted, #6B7280);
    }

    /* ── Status badge ── */
    .status-badge {
      font-size: var(--ap-fs-micro, 11px);
      padding: 2px 8px;
      border-radius: 99px;
      font-weight: 600;
      letter-spacing: 0.02em;
      white-space: nowrap;
      text-transform: uppercase;
    }

    .status-badge.Active   { background: var(--ap-success-soft, #E3F4EC); color: var(--ap-success, #047A55); }
    .status-badge.Archived { background: var(--ap-surface-muted, #EEF0F3); color: var(--ap-text-muted, #6B7280); }
    .status-badge.Deleted  { background: var(--ap-danger-soft, #FDECEA);  color: var(--ap-danger, #B42318); }
    .status-badge.open     { background: var(--ap-success-soft, #E3F4EC); color: var(--ap-success, #047A55); }
    .status-badge.archived { background: var(--ap-surface-muted, #EEF0F3); color: var(--ap-text-muted, #6B7280); }
    .status-badge.deleted  { background: var(--ap-danger-soft, #FDECEA);  color: var(--ap-danger, #B42318); }

    /* ── MC badge ── */
    .mc-badge {
      background: #B45309;
      color: #fff;
      border: none;
      padding: 2px 7px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      line-height: 1.5;
    }

    /* ── Responsive ── */
    @media (max-width: 768px) {
      .conversations-header { flex-direction: column; align-items: flex-start; padding: 10px 0; }
      .header-right { width: 100%; }
      .contact-group-header { padding: 10px 12px; }
      .conversation-sub-row { padding: 9px 12px 9px 54px; }
      .conversation-sub-row.has-unread { padding-left: 52px; }
    }

    @media (max-width: 480px) {
      .contact-avatar { width: 32px; height: 32px; font-size: 11px; }
      .contact-name-row { font-size: 13px; }
      .contact-subtitle { font-size: 10px; }
      .conversation-sub-row { padding: 8px 8px 8px 46px; gap: 8px; }
      .conversation-sub-row.has-unread { padding-left: 44px; }
      .conv-channel-chip { width: 76px; font-size: 10px; padding: 2px 5px 2px 4px; }
      .chip-icon { width: 10px; height: 10px; }
      .status-badge { font-size: 9px; padding: 1px 5px; }
    }
  `]
})
export class ConversationsComponent implements OnInit, OnDestroy {
  @Input() statusFilter: ConversationFilterStatus = CONVERSATION_CONSTANTS.STATUS.ALL as ConversationFilterStatus;

  public conversations: Conversation[] = [];
  public rcConversations: Conversation[] = [];
  public mcConversations: Conversation[] = [];
  public activeConversation: Conversation | null = null;
  public loading: boolean = true;
  public filters: ConversationFilters = {
    status: CONVERSATION_CONSTANTS.STATUS.ALL as ConversationFilterStatus
  };

  public contactGroups: ContactGroup[] = [];
  private channelTypeLookup: { [channelId: string]: string } = {};
  private expandedGroups: Set<string> = new Set();

  private subscriptions: Subscription = new Subscription();

  constructor(
    private conversationService: ConversationService,
    private authService: AuthService,
    private rcOversightService: RCOversightService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    if (this.statusFilter) {
      this.filters.status = this.statusFilter;
    }

    this.loadChannels();
    this.loadConversations();

    this.subscriptions.add(
      this.conversationService.activeConversation$.subscribe(conversation => {
        this.activeConversation = conversation;
      })
    );

    this.subscriptions.add(
      this.conversationService.conversationUpdated$.subscribe(updatedConversation => {
        const index = this.conversations.findIndex(c => c.id === updatedConversation.id);
        if (index !== -1) {
          this.conversations[index] = { ...this.conversations[index], ...updatedConversation };
          this.conversations = [...this.conversations];
          this.buildContactGroups();
        }
      })
    );

    this.subscriptions.add(
      this.conversationService.newMessage$.subscribe(event => {
        if (event.conversationId && event.action === 'created') {
          this.refreshConversationInList(event.conversationId);
          if (this.activeConversation?.id === event.conversationId) {
            this.conversationService.markConversationAsRead(event.conversationId);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleFilterChange(): void {
    this.loadConversations();
  }

  public handleCloseConversation(): void {
    this.conversationService.setActiveConversation(null);
  }

  public isConversationActive(conversation: Conversation): boolean {
    return this.activeConversation?.id === conversation.id;
  }

  // ── Grouping ────────────────────────────────────────────────────────────────

  private buildContactGroups(): void {
    const groupMap = new Map<string, ContactGroup>();

    for (const conv of this.conversations) {
      const contactId = conv.contact?.id || (conv as any).initiator_id || conv.contact?.mobile || 'unknown';
      const contactName = conv.contact?.name || 'Unknown';
      const contactMobile = conv.contact?.mobile || '';

      if (!groupMap.has(contactId)) {
        groupMap.set(contactId, {
          contactId,
          contactName,
          contactMobile,
          avatarInitials: this.getAvatarInitials(contactName),
          avatarColorIndex: this.getAvatarColorIndex(contactId),
          totalUnread: 0,
          conversations: [],
          isMC: this.isMCConversation(conv),
          mcCompanyName: (conv as any).mcCompanyName || (conv as any).company_name || undefined
        });
        if (!this.expandedGroups.has(contactId)) {
          this.expandedGroups.add(contactId);
        }
      }

      const group = groupMap.get(contactId)!;
      group.conversations.push(conv);
      group.totalUnread += conv.unread_count || 0;
    }

    // Sort conversations within each group by last message time (most recent first)
    for (const group of groupMap.values()) {
      group.conversations.sort((a, b) => this.getMessageTimestamp(b) - this.getMessageTimestamp(a));
    }

    // Sort groups by most recent message time
    this.contactGroups = Array.from(groupMap.values()).sort((a, b) => {
      const aTime = a.conversations[0] ? this.getMessageTimestamp(a.conversations[0]) : 0;
      const bTime = b.conversations[0] ? this.getMessageTimestamp(b.conversations[0]) : 0;
      return bTime - aTime;
    });
  }

  private getMessageTimestamp(conv: Conversation): number {
    const t = (conv.lastMessage as any)?.created_at || conv.lastMessage?.datetime || conv.updated_at || conv.created_at;
    return t ? new Date(t as any).getTime() : 0;
  }

  public toggleGroup(contactId: string): void {
    if (this.expandedGroups.has(contactId)) {
      this.expandedGroups.delete(contactId);
    } else {
      this.expandedGroups.add(contactId);
    }
  }

  public isGroupExpanded(contactId: string): boolean {
    return this.expandedGroups.has(contactId);
  }

  public getTotalConversationsCount(): number {
    return this.conversations.length;
  }

  public getTotalContactsCount(): number {
    return this.contactGroups.length;
  }

  public trackByContactId(_: number, group: ContactGroup): string {
    return group.contactId;
  }

  public trackByConvId(_: number, conv: Conversation): string {
    return conv.id;
  }

  // ── Channel helpers ──────────────────────────────────────────────────────────

  public getChannelType(channelId?: string): string {
    if (!channelId) return '';
    return this.channelTypeLookup[channelId] || '';
  }

  private loadChannels(): void {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser?.company_id) return;

    this.http.get<any>(`${config.apiUrl}/api/channels?company_id=${currentUser.company_id}`).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.channelTypeLookup = {};
          response.data.forEach((ch: any) => {
            if (ch.id && ch.channel_type) {
              this.channelTypeLookup[ch.id] = ch.channel_type;
            }
          });
          // Rebuild groups with updated channel info (if conversations already loaded)
          if (this.contactGroups.length > 0) {
            this.buildContactGroups();
          }
        }
      },
      error: (err) => console.error('[ConversationsComponent] Error loading channels:', err)
    });
  }

  // ── Avatar helpers ───────────────────────────────────────────────────────────

  public getAvatarInitials(name: string): string {
    if (!name || name === 'Unknown') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }

  private getAvatarColorIndex(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = ((hash << 5) - hash) + id.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % 7;
  }

  // ── Relative time ────────────────────────────────────────────────────────────

  public getRelativeTime(dateInput?: Date | string | null): string {
    if (!dateInput) return '';
    const date = new Date(dateInput as any);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHours < 24) return `${diffHours}h`;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yest';

    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // ── Conversation loading ─────────────────────────────────────────────────────

  private refreshConversationInList(conversationId: string): void {
    this.subscriptions.add(
      this.conversationService.getConversationById(conversationId).subscribe({
        next: (updatedConversation) => {
          const index = this.conversations.findIndex(c => c.id === conversationId);
          if (index !== -1) {
            this.conversations[index] = updatedConversation;
            this.conversations.sort((a, b) => this.getMessageTimestamp(b) - this.getMessageTimestamp(a));
            this.conversations = [...this.conversations];
          } else {
            this.conversations.unshift(updatedConversation);
          }
          this.buildContactGroups();
        },
        error: (error) => {
          console.error(`[ConversationsComponent] Error refreshing conversation ${conversationId}:`, error);
        }
      })
    );
  }

  private loadConversations(): void {
    this.loading = true;
    if (this.isSuperAdmin()) {
      this.loadRCAndMCConversations();
    } else {
      this.loadRegularConversations();
    }
  }

  private loadRCAndMCConversations(): void {
    let rcLoaded = false;
    let mcLoaded = false;

    const checkBothLoaded = () => {
      if (rcLoaded && mcLoaded) {
        this.conversations = [...this.rcConversations, ...this.mcConversations];
        this.buildContactGroups();
        this.loading = false;
      }
    };

    this.subscriptions.add(
      this.conversationService.getConversations(this.filters).subscribe({
        next: (rcConversations) => {
          this.rcConversations = rcConversations.map(conv => ({ ...conv, isRC: true }));
          rcLoaded = true;
          checkBothLoaded();
        },
        error: (error) => {
          console.error('Error fetching RC conversations:', error);
          this.rcConversations = [];
          rcLoaded = true;
          checkBothLoaded();
        }
      })
    );

    this.subscriptions.add(
      this.rcOversightService.getMCConversations().subscribe({
        next: (response) => {
          if (response && response.success) {
            this.mcConversations = (response.data || []).map((conv: any) => ({
              ...conv,
              id: conv.id || conv._id,
              isMC: true,
              mcCompanyName: conv.company_name || 'Unknown MC',
              status: conv.status || 'Active',
              created_at: conv.created_at || conv.createdAt,
              updated_at: conv.updated_at || conv.updatedAt
            }));
          } else {
            this.mcConversations = [];
          }
          mcLoaded = true;
          checkBothLoaded();
        },
        error: (error) => {
          console.error('Error fetching MC conversations:', error);
          this.mcConversations = [];
          mcLoaded = true;
          checkBothLoaded();
        }
      })
    );
  }

  private loadRegularConversations(): void {
    this.subscriptions.add(
      this.conversationService.getConversations(this.filters).subscribe({
        next: (conversations) => {
          this.conversations = conversations;
          this.buildContactGroups();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching conversations:', error);
          this.loading = false;
        }
      })
    );
  }

  // ── Role / MC helpers ────────────────────────────────────────────────────────

  public isSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    if (currentUser?.role !== UserRole.OWNER) return false;
    // company_type may be missing from older tokens — super_admin role is RC-only
    return !currentUser?.company_type || currentUser.company_type === 'RC';
  }

  public isMCConversation(conversation: any): boolean {
    if (conversation?.isMC === true) return true;
    // Detect via company_id mismatch: conversation belongs to a different company than the logged-in user
    const currentUser = this.authService.currentUserValue;
    if (!currentUser?.company_id) return false;
    const convCompanyId = (conversation as any)?.company_id;
    return !!convCompanyId && convCompanyId !== currentUser.company_id;
  }

  public getMCCompanyName(conversation: any): string {
    return conversation?.mcCompanyName || 'Unknown MC';
  }

  public handleConversationClick(conversation: Conversation): void {
    if (this.isMCConversation(conversation)) {
      this.conversationService.setActiveConversation({ ...conversation, isReadOnly: true });
    } else {
      this.conversationService.setActiveConversation(conversation);
    }

    if (conversation.unread_count && conversation.unread_count > 0) {
      setTimeout(() => {
        this.conversationService.markConversationAsRead(conversation.id);
      }, 0);
    }
  }
}
